import { Mic, Sparkles, Square, Upload, Wand2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { UploadProgressBar } from '@/components/ui/UploadProgressBar';
import { api } from '@/lib/api';
import { buildStepContext } from '@/lib/narration-context';
import type { RevealStep, RevealStepAudio, Slide } from '@/lib/schema.types';
import { uploadFile } from '@/lib/uploads';
import { cn } from '@/lib/utils';

type Tab = 'tts' | 'record' | 'upload';

const VOICE_OPTIONS = [
  { id: 'james', label: 'James (Standard English)' },
  { id: 'aria', label: 'Aria (Warm Female)' },
  { id: 'noah', label: 'Noah (Friendly Male)' },
  { id: 'leah', label: 'Leah (British English)' },
];

interface Props {
  step: RevealStep | null;
  slide: Slide | null;
  onUpdateAudio: (audio: RevealStepAudio) => void;
}

export function AudioEditor({ step, slide, onUpdateAudio }: Props) {
  const audio = step?.audio;
  const initialTab: Tab = audio?.source ?? 'tts';
  const [tab, setTab] = useState<Tab>(initialTab);

  useEffect(() => {
    setTab(audio?.source ?? 'tts');
  }, [step?.id, audio?.source]);

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="border-b border-border-subtle px-5 py-4">
        <h3 className="text-base font-semibold text-fg">Audio Editor</h3>
        <p className="mt-0.5 text-[11px] text-fg-muted">
          {step ? `Editing ${step.label || 'step'}` : 'Select a step to edit its narration'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 scroll-thin">
        {!step ? (
          <EmptyState />
        ) : (
          <>
            <Tabs
              value={tab}
              onChange={(t) => setTab(t)}
              tabs={[
                { id: 'tts', label: 'GENERATE' },
                { id: 'record', label: 'RECORD' },
                { id: 'upload', label: 'UPLOAD' },
              ]}
            />

            <div className="mt-4 space-y-5">
              {tab === 'tts' && (
                <TtsPanel step={step} slide={slide} onUpdate={onUpdateAudio} />
              )}
              {tab === 'record' && <RecordPanel step={step} onUpdate={onUpdateAudio} />}
              {tab === 'upload' && <UploadPanel step={step} onUpdate={onUpdateAudio} />}

              <SettingsPanel step={step} onUpdate={onUpdateAudio} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-md border border-dashed border-border bg-bg-subtle/40 p-6 text-center text-xs text-fg-muted">
      Select or create a step in the timeline to attach narration.
    </div>
  );
}

// ---------- TTS ----------

// Our self-hosted model. Single fixed Hinglish voice (no voice picker), and the
// only engine whose clips get bundled for the offline Electron classroom.
const CUSTOM_ENGINE = 'voxcpm';
const ENGINE_OPTIONS = [
  { id: CUSTOM_ENGINE, label: 'VoxCPM — Hinglish (offline, default)' },
  { id: 'elevenlabs', label: 'ElevenLabs' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'google', label: 'Google Cloud TTS' },
];

function TtsPanel({
  step,
  slide,
  onUpdate,
}: {
  step: RevealStep;
  slide: Slide | null;
  onUpdate: (a: RevealStepAudio) => void;
}) {
  const audio = step.audio ?? {};
  const [text, setText] = useState(audio.text ?? '');
  const [voice, setVoice] = useState(audio.voiceModel ?? 'james');
  const [engine, setEngine] = useState<string>(CUSTOM_ENGINE);
  const [busy, setBusy] = useState(false);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [language, setLanguage] = useState('English');
  const [tone, setTone] = useState('warm and conversational');
  const [targetSeconds, setTargetSeconds] = useState(12);
  const [rewriteInstruction, setRewriteInstruction] = useState('');

  useEffect(() => {
    setText(step.audio?.text ?? '');
    setVoice(step.audio?.voiceModel ?? 'james');
    setRewriteInstruction('');
  }, [step.id, step.audio?.text, step.audio?.voiceModel]);

  const writeWithClaude = async () => {
    if (!slide) return;
    setWriting(true);
    setError(null);
    try {
      const ctx = buildStepContext(slide, step);
      const draft = text.trim();
      const res = await api.ai.generateNarrationText({
        slideLabel: ctx.slideLabel,
        stepLabel: ctx.stepLabel,
        elements: ctx.elements,
        previousNarrations: ctx.previousNarrations,
        tone,
        language,
        targetSeconds,
        currentDraft: draft || undefined,
        rewriteInstruction: draft ? rewriteInstruction.trim() || undefined : undefined,
      });
      setText(res.text);
      onUpdate({ ...audio, source: 'tts', text: res.text });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate narration');
    } finally {
      setWriting(false);
    }
  };

  const generate = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.ai.generateNarration({
        text,
        voiceModel: voice,
        playbackSpeed: audio.playbackSpeed ?? 1,
        engine,
      });
      onUpdate({
        ...audio,
        source: 'tts',
        src: res.src,
        text: res.text,
        // The Devanagari actually spoken (custom engine only); record the text
        // this clip was made from so a later edit flags it as stale.
        devnagariText: res.devnagariText,
        audioForText: res.text,
        durationMs: res.durationMs,
        voiceModel: res.voiceModel,
        playbackSpeed: res.playbackSpeed,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setBusy(false);
    }
  };

  // When the audio element loads metadata we know the true duration —
  // refine the stored value so the timeline / trim slider line up.
  const onAudioLoaded = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const el = e.currentTarget;
    const ms = Math.round((el.duration || 0) * 1000);
    if (ms > 0 && Math.abs(ms - (audio.durationMs ?? 0)) > 200) {
      onUpdate({ ...step.audio, durationMs: ms });
    }
  };

  return (
    <div className="space-y-3">
      <Field label="Text to speech">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder="Write the narration script for this step, or generate it with Claude…"
          className="w-full rounded-md border border-border bg-white p-2.5 text-sm text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
        />
      </Field>

      {/* Read-only: the pure-Devanagari the offline voice model actually speaks. */}
      {audio.devnagariText && (
        <div>
          <span className="mb-1.5 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-fg-subtle">
            Spoken (Devanagari)
          </span>
          <p className="rounded-md border border-border-subtle bg-bg-subtle/50 p-2.5 text-sm leading-relaxed text-fg-muted">
            {audio.devnagariText}
          </p>
        </div>
      )}

      {/* Stale-audio warning: the clip was generated from different text. */}
      {audio.src &&
        audio.audioForText &&
        text.trim() !== audio.audioForText.trim() && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11.5px] text-amber-800">
            Narration text changed — regenerate the audio so it matches.
          </p>
        )}

      <div className="rounded-md border border-border-subtle bg-bg-subtle/40 p-2.5">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-fg-muted">
            Write with Claude
          </span>
          <span className="text-[10px] text-fg-subtle">uses slide context</span>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <Field label="Tone">
            <input
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="h-8 w-full rounded border border-border bg-white px-2 text-[12px] text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              placeholder="warm, crisp"
            />
          </Field>
          <Field label="Language">
            <input
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="h-8 w-full rounded border border-border bg-white px-2 text-[12px] text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              placeholder="English"
            />
          </Field>
          <Field label="Length (s)">
            <input
              type="number"
              min={3}
              max={60}
              value={targetSeconds}
              onChange={(e) => setTargetSeconds(Math.max(3, Math.min(60, Number(e.target.value) || 12)))}
              className="h-8 w-full rounded border border-border bg-white px-2 text-[12px] text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </Field>
        </div>
        {text.trim() && (
          <div className="mt-2">
            <Field label="Rewrite instruction (optional)">
              <input
                value={rewriteInstruction}
                onChange={(e) => setRewriteInstruction(e.target.value)}
                placeholder="e.g. make it shorter, add an analogy, simplify"
                className="h-8 w-full rounded border border-border bg-white px-2 text-[12px] text-fg focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              />
            </Field>
          </div>
        )}
        <Button
          variant="soft"
          className="mt-2 w-full justify-center"
          onClick={writeWithClaude}
          disabled={!slide || writing}
        >
          <Wand2 size={13} /> {writing ? 'Writing…' : text.trim() ? 'Rewrite with Claude' : 'Write with Claude'}
        </Button>
      </div>

      <Button
        variant="soft"
        className="w-full justify-center"
        onClick={() => onUpdate({ ...audio, source: 'tts', text })}
        disabled={!text.trim()}
      >
        Update captions
      </Button>

      <div className={engine === CUSTOM_ENGINE ? '' : 'grid grid-cols-2 gap-2'}>
        <Field label="Engine">
          <Select value={engine} onChange={(e) => setEngine(e.target.value)}>
            {ENGINE_OPTIONS.map((v) => (
              <option key={v.id} value={v.id}>
                {v.label}
              </option>
            ))}
          </Select>
        </Field>
        {/* VoxCPM has one fixed Hinglish voice — no voice picker for it. */}
        {engine !== CUSTOM_ENGINE && (
          <Field label="Voice model">
            <Select value={voice} onChange={(e) => setVoice(e.target.value)}>
              {VOICE_OPTIONS.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>

      {error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
      )}

      <Button
        variant="primary"
        className="mt-2 w-full justify-center text-sm"
        onClick={generate}
        disabled={!text.trim() || busy}
      >
        <Sparkles size={14} /> {busy ? 'Generating…' : 'Generate audio'}
      </Button>

      {audio.src && audio.source === 'tts' && (
        <div className="space-y-1.5 rounded-md border border-border-subtle bg-bg-subtle/40 p-2">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.14em] text-fg-muted">
            Generated narration
          </span>
          <audio
            key={audio.src}
            src={audio.src}
            controls
            onLoadedMetadata={onAudioLoaded}
            className="w-full"
          />
        </div>
      )}
    </div>
  );
}

// ---------- Record ----------

function RecordPanel({
  step,
  onUpdate,
}: {
  step: RevealStep;
  onUpdate: (a: RevealStepAudio) => void;
}) {
  const audio = step.audio ?? {};
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);

  useEffect(() => {
    return () => {
      recorderRef.current?.state === 'recording' && recorderRef.current.stop();
    };
  }, []);

  const start = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const mime = chunksRef.current[0]?.type || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mime });
        stream.getTracks().forEach((t) => t.stop());
        const durationMs = Date.now() - startedAtRef.current;
        setUploading(true);
        setProgress(0);
        try {
          const uploaded = await uploadFile(blob, {
            type: 'NARRATION',
            name: `narration-${Date.now()}`,
            contentType: mime,
            extension: mime.includes('webm')
              ? 'webm'
              : mime.includes('mp4') || mime.includes('m4a')
                ? 'm4a'
                : mime.includes('ogg')
                  ? 'ogg'
                  : 'webm',
            onProgress: setProgress,
          });
          onUpdate({
            ...audio,
            source: 'record',
            src: uploaded.url,
            durationMs,
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Upload failed');
        } finally {
          setUploading(false);
        }
      };
      startedAtRef.current = Date.now();
      recorder.start();
      setRecording(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Microphone unavailable');
    }
  };

  const stop = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-fg-muted">
        Record narration directly from your microphone — the clip uploads to S3 and the
        hosted URL is attached to this step.
      </p>
      {error && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
      )}
      {!recording ? (
        <Button
          variant="primary"
          className="w-full justify-center"
          onClick={start}
          disabled={uploading}
        >
          <Mic size={14} /> {uploading ? 'Uploading…' : 'Start recording'}
        </Button>
      ) : (
        <Button variant="soft" className="w-full justify-center" onClick={stop}>
          <Square size={12} /> Stop recording
        </Button>
      )}
      {uploading && <UploadProgressBar value={progress} />}
      {audio.src && audio.source === 'record' && !uploading && (
        <audio src={audio.src} controls className="w-full" />
      )}
    </div>
  );
}

// ---------- Upload ----------

function UploadPanel({
  step,
  onUpdate,
}: {
  step: RevealStep;
  onUpdate: (a: RevealStepAudio) => void;
}) {
  const audio = step.audio ?? {};
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (file: File) => {
    setError(null);
    setUploading(true);
    setProgress(0);
    try {
      const uploaded = await uploadFile(file, {
        type: 'NARRATION',
        onProgress: setProgress,
      });
      onUpdate({ ...audio, source: 'upload', src: uploaded.url });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <input
        ref={fileRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
      <button
        type="button"
        disabled={uploading}
        onClick={() => fileRef.current?.click()}
        className="flex h-32 w-full flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border bg-bg-subtle/40 text-fg-muted transition hover:border-accent/60 hover:text-accent disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Upload size={18} />
        <span className="text-sm font-medium">
          {uploading ? 'Uploading…' : 'Upload an audio file'}
        </span>
        <span className="text-[11px] text-fg-subtle">.mp3, .wav, .webm — uploaded to S3</span>
      </button>
      {uploading && <UploadProgressBar value={progress} />}
      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>}
      {audio.src && audio.source === 'upload' && !uploading && (
        <audio src={audio.src} controls className="w-full" />
      )}
    </div>
  );
}

// ---------- Common settings ----------

function SettingsPanel({
  step,
  onUpdate,
}: {
  step: RevealStep;
  onUpdate: (a: RevealStepAudio) => void;
}) {
  const audio = step.audio ?? {};
  const speed = audio.playbackSpeed ?? 1;

  return (
    <div className="space-y-4 border-t border-border-subtle pt-4">
      <div>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-fg-muted">
            Playback speed
          </span>
          <span className="font-mono text-[11px] text-fg-muted">{speed.toFixed(2)}x</span>
        </div>
        <input
          type="range"
          min={0.5}
          max={2}
          step={0.05}
          value={speed}
          onChange={(e) => onUpdate({ ...audio, playbackSpeed: Number(e.target.value) })}
          className="w-full"
        />
      </div>
    </div>
  );
}

// ---------- helpers ----------

function Tabs({
  value,
  onChange,
  tabs,
}: {
  value: Tab;
  onChange: (t: Tab) => void;
  tabs: { id: Tab; label: string }[];
}) {
  return (
    <div className="grid grid-cols-3 rounded-md border border-border bg-bg-subtle p-0.5">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={cn(
            'h-8 rounded text-[11px] font-semibold uppercase tracking-wider transition',
            value === t.id ? 'bg-white text-accent shadow-pill' : 'text-fg-muted hover:text-fg',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.14em] text-fg-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
